<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import Swiper from 'swiper'
import { Autoplay, Pagination } from 'swiper/modules'
import type { ChapterConfig } from '../configs/types'
import { getChapterConfig } from '../configs/brands'
import { usePurchase } from '../composables/usePurchase'
import { useSubscription } from '../composables/useSubscription'
import { createShowPaymentMethodSelector } from '../composables/usePaymentMethodDisplay'
import SubscribeItemContentDefault from './SubscribeItemContent.flipnovel.vue'
import SubscribeItemContentNovaris from './SubscribeItemContent.novaris.vue'
import PurchaseItemContent from './PurchaseItemContent.flipnovel.vue'
import PaymentMethodSelector from './PaymentMethodSelector.flipnovel.vue'
import { useAxonViewItem } from '@/composables/axon/useAxonViewItem'
import { useFbqPaymentPopupAddToCart } from '@/composables/fbq/useFbqPaymentPopupAddToCart'
import { useUserStore } from '@/stores'
import 'swiper/css'
import 'swiper/css/pagination'
import { brand, colors } from '@/config/brands'

const props = defineProps<{
  value: boolean
  contentId: string
  contentChapterId: string
  bookName: string
  chapterName: string
  serialStatus: number
}>()

const emit = defineEmits<{
  (e: 'input', value: boolean): void
  (e: 'success'): void
}>()

const config: ChapterConfig = getChapterConfig()
const useSubscribeItemContentNovaris = brand === 'novaris' || brand === 'storlyn'
const SubscribeItemContentComponent = useSubscribeItemContentNovaris ? SubscribeItemContentNovaris : SubscribeItemContentDefault

const userStore = useUserStore()

const {
  templateProducts,
  PayTemplateSuccess,
  PayTemplate,
  currentPaymentMethod: purchasePaymentMethod,
  availablePaymentMethods: purchaseAvailableMethods,
  selectPaymentMethod: selectPurchasePaymentMethod,
  productLists: purchaseProductLists,
} = usePurchase('chapter', {
  contentId: props.contentId,
  contentChapterId: props.contentChapterId,
  bookName: props.bookName,
  chapterName: props.chapterName,
  serialStatus: props.serialStatus,
})

const {
  loaded,
  subscribeSuccess,
  getUnitPrice,
  onSubscribeProducts,
  isZero,
  availablePaymentMethods: subscriptAvailableMethods,
  selectPaymentMethod: selectSubscriptPaymentMethod,
  currentPaymentMethod: subscriptPaymentMethod,
  hasPayPalPasswordlessPayment,
} = useSubscription('chapter', {
  contentId: props.contentId,
  contentChapterId: props.contentChapterId,
})

const active = ref(0)
const swiperInstance = ref<Swiper | null>(null)
const swiperInitTimer = ref<NodeJS.Timeout | null>(null)

const currentPaymentMethod = computed({
  get: () => {
    if (!purchaseProductLists.value || purchaseProductLists.value.length === 0)
      return subscriptPaymentMethod.value
    return purchasePaymentMethod.value
  },
  set: (val) => {
    selectPurchasePaymentMethod(val)
    selectSubscriptPaymentMethod(val)
  },
})

const availablePaymentMethods = computed(() => {
  if (hasPayPalPasswordlessPayment.value) {
    const all = [...(purchaseAvailableMethods.value || []), ...(subscriptAvailableMethods.value || [])]
    const mergedUnique = all.filter((m, i, arr) =>
      arr.findIndex(x => String(x.paymentMethod).toLowerCase() === String(m.paymentMethod).toLowerCase()) === i,
    )
    return mergedUnique.filter(m => String(m.paymentMethod).toLowerCase() === 'paypal')
  }

  const purchaseMethods = purchaseAvailableMethods.value || []
  const subscriptMethods = subscriptAvailableMethods.value || []

  if (purchaseMethods.length > subscriptMethods.length)
    return purchaseMethods
  if (subscriptMethods.length > purchaseMethods.length)
    return subscriptMethods

  if (purchaseMethods.length === 1 && subscriptMethods.length === 1) {
    const p = String(purchaseMethods[0]?.paymentMethod ?? '')
    const s = String(subscriptMethods[0]?.paymentMethod ?? '')
    if (p === s)
      return purchaseMethods
    const merged = [...purchaseMethods, ...subscriptMethods].filter(
      (m, i, arr) => arr.findIndex(x => String(x.paymentMethod) === String(m.paymentMethod)) === i,
    )
    return merged
  }

  return purchaseMethods
})

const showPaymentMethodSelector = createShowPaymentMethodSelector(availablePaymentMethods)

const subscribes = computed(() => {
  const currentMethod = Number(currentPaymentMethod.value)
  if (!subscriptAvailableMethods.value || !Array.isArray(subscriptAvailableMethods.value))
    return []
  const currentMethodList = subscriptAvailableMethods.value.find(
    (list: any) => Number(list?.paymentMethod) === currentMethod,
  )
  if (!currentMethodList?.products || !Array.isArray(currentMethodList.products))
    return []
  return currentMethodList.products.filter((item: any) => item && item.purchasePopShow)
})

const hasOnlySubscribes = computed(() => {
  return (!templateProducts.value || templateProducts.value.length === 0) && subscribes.value?.length > 0
})

function handlePayTemplate(item: any, index: number) {
  active.value = index
  PayTemplate(item)
}

function handleSubscribeProducts(item: any) {
  selectSubscriptPaymentMethod(currentPaymentMethod.value)
  onSubscribeProducts(item)
}

function initSwiper() {
  if (swiperInitTimer.value) {
    clearTimeout(swiperInitTimer.value)
    swiperInitTimer.value = null
  }

  if (swiperInstance.value) {
    swiperInstance.value.destroy(true, true)
    swiperInstance.value = null
  }

  if (!subscribes.value?.length || hasOnlySubscribes.value)
    return

  nextTick(() => {
    if (!subscribes.value?.length || hasOnlySubscribes.value)
      return

    swiperInstance.value = new Swiper('.banner.swiper', {
      modules: [Pagination, Autoplay],
      spaceBetween: 0,
      autoplay: {
        delay: 3500,
        disableOnInteraction: false,
      },
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
      loop: subscribes.value.length > 1,
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      resizeObserver: true,
    })
  })
}

function shouldInitSwiper() {
  return loaded.value && props.value && subscribes.value?.length > 0 && !hasOnlySubscribes.value
}

watch(
  () => props.value,
  (newVal) => {
    if (newVal && shouldInitSwiper())
      initSwiper()
  },
)

watch(
  () => loaded.value,
  (val) => {
    if (val && shouldInitSwiper())
      initSwiper()
  },
)

watch(
  () => subscribes.value,
  (newVal) => {
    if (loaded.value && newVal?.length && props.value && !hasOnlySubscribes.value)
      initSwiper()
  },
)

watch(
  currentPaymentMethod,
  () => {
    if (shouldInitSwiper()) {
      initSwiper()
    }
    else {
      if (swiperInstance.value) {
        swiperInstance.value.destroy(true, true)
        swiperInstance.value = null
      }
    }
  },
)

function handleVisibilityChange() {
  if (!document.hidden && shouldInitSwiper())
    initSwiper()
}

onMounted(() => {
  document.addEventListener('visibilitychange', handleVisibilityChange)
})

onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  if (swiperInstance.value) {
    swiperInstance.value.destroy(true, true)
    swiperInstance.value = null
  }
})

watch(
  () => subscribeSuccess.value,
  (val) => {
    if (val)
      emit('success')
  },
)

watch(
  () => PayTemplateSuccess.value,
  (val) => {
    if (val)
      emit('success')
  },
)

const { reportViewItem: reportPurchaseViewItem } = useAxonViewItem()
const { reportViewItem: reportSubscribeViewItem } = useAxonViewItem()

watch(templateProducts, (val) => {
  if (val.length)
    reportPurchaseViewItem(val, currentPaymentMethod.value, 'Coins Package')
})

watch(subscribes, (val) => {
  if (val.length)
    reportSubscribeViewItem(val, currentPaymentMethod.value, 'Membership', p => p.preferentialPrice ?? p.price)
})

const { reportAll: reportFbqAddToCartAll } = useFbqPaymentPopupAddToCart()
watch([purchaseProductLists, subscribes], () => {
  if (purchaseProductLists.value?.length || subscribes.value?.length)
    reportFbqAddToCartAll(purchaseProductLists.value ?? [], [{ products: subscribes.value }])
})

const vipBg = config.colors.vipBg
const pinkGradient = config.colors.pinkGradient
const subscribeCardBg = config.colors.subscribeCardBg || 'linear-gradient(90deg, #443431, #261b1a)'
const purchaseActiveBg = config.colors.purchaseActiveBg || '#222936'

function getSubscribeItemStyle(item: any) {
  if (useSubscribeItemContentNovaris) {
    return {
      background: 'transparent',
      border: 'none',
    }
  }

  return {
    background: subscribeCardBg,
    backgroundSize: '100% 100%',
    border: `1px solid ${colors?.border.primary}`,
  }
}
</script>

<template>
  <BasicPopup :value="value" @input="value => $emit('input', value)">
    <div class="popup-recharge w-full px-5 pt-20px pb-30px bg-n-bg-card rounded-t-lg relative" :class="{ 'is-novaris': brand === 'novaris' }">
      <h3 class="text-18px text-center text-n-text-1">
        {{ config.texts.payTemplateTitle }}
      </h3>
      <p class="text-14px text-n-text-2 text-center">
        {{ config.texts.payTemplateDesc }}
      </p>
      <template v-if="showPaymentMethodSelector">
        <PaymentMethodSelector
          :dark="config.darkPaymentSelector"
          :model-value="currentPaymentMethod"
          :available-methods="availablePaymentMethods"
          @update:model-value="val => currentPaymentMethod = val"
        />
      </template>

      <div class="flex flex-wrap justify-between">
        <div
          v-for="(item, index) in templateProducts"
          :key="index"
          class="purchase-item-wrapper relative b-rd-8px w-48% mt-4"
          :style="{
            border: active === index ? `1px solid ${colors?.primary}` : `1px solid ${colors?.border.primary}`,
            background: active === index ? purchaseActiveBg : colors?.bg.card,
          }"
          @click="handlePayTemplate(item, index)"
        >
          <PurchaseItemContent :item="item" :is-active="active === index" />
        </div>
      </div>

      <div v-if="!userStore.$state.subscribe && subscribes?.length && hasOnlySubscribes" class="subscribe-list-container mt-4">
        <div class="subscribe-list">
          <div
            v-for="(item, index) in subscribes"
            :key="index"
            class="subscribe-list-item rounded-8px mb-3"
            :class="{ 'subscribe-list-item-novaris': useSubscribeItemContentNovaris }"
            :style="getSubscribeItemStyle(item)"
            @click="handleSubscribeProducts(item)"
          >
            <component :is="SubscribeItemContentComponent" :item="item" :get-unit-price="getUnitPrice" :is-zero="isZero" />
          </div>
        </div>
      </div>

      <div v-else-if="!userStore.$state.subscribe && subscribes?.length" class="banner swiper mt-4">
        <div class="swiper-wrapper">
          <div
            v-for="(item, index) in subscribes"
            :key="index"
            class="swiper-slide swiper-item rounded-8px"
            :class="{ 'swiper-item-novaris': useSubscribeItemContentNovaris }"
            :style="getSubscribeItemStyle(item)"
            @click="handleSubscribeProducts(item)"
          >
            <component :is="SubscribeItemContentComponent" :item="item" :get-unit-price="getUnitPrice" :is-zero="isZero" />
          </div>
        </div>
        <div class="swiper-pagination flex items-center justify-center" />
      </div>

      <div v-if="!loaded" class="h-260px flex items-center justify-center flex-col">
        <BasicSpinner class="mx-auto" />
      </div>
    </div>
  </BasicPopup>
</template>

<style lang="scss" scoped>
.pay-template-info:hover {
  border: 1px solid var(--primary-color);
  background-color: v-bind(vipBg);
}

.swiper-item {
  height: 104px;
}

@media screen and (min-width: 380px) {
  .swiper-item {
    min-height: 82px;
  }
}

.subscribe-list-container {
  max-height: 360px;
  overflow: hidden;
  position: relative;
}

.subscribe-list {
  max-height: 360px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 10px;
  width: 100%;
  box-sizing: border-box;
  overscroll-behavior: contain;
  touch-action: pan-y;
}

.subscribe-list-item {
  min-height: 82px;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.02);
  }

  &:last-child {
    margin-bottom: 0 !important;
  }
}

.subscribe-list-item-novaris {
  min-height: 92px;
  overflow: hidden;

  &:hover {
    transform: none;
  }
}

.swiper-item-novaris {
  height: 92px;
  overflow: hidden;
}
</style>

<style lang="scss">
.popup-recharge {
  .swiper-pagination-bullet {
    width: 5px;
    height: 5px;
    background: #ffffff;
    border-radius: 999px;
    opacity: 0.5;
  }

  .swiper-pagination-bullet-active {
    width: 10px;
    height: 5px;
    background: #ffffff;
    border-radius: 999px;
    opacity: 1;
  }

  .popular-tag {
    position: absolute;
    top: 0;
    left: 0;
    height: 18px;
    padding: 0 8px;
    background: linear-gradient(132deg, #F961BC 0%, #F74984 100%);
    border-radius: 100px 100px 100px 0px;
    font-weight: 400;
    font-size: 10px;
    color: white !important;
    text-align: center;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
  }

  .marketing-tag {
    justify-content: center !important;
    padding-left: 8px !important;
    padding-right: 8px !important;
  }

  &.is-novaris {
    .payment-method-selector {
      background: #35224D;

      .payment-method-btn.active {
        background: #8E47FF;
        box-shadow: none;

        > div {
          background: #3e2f53 !important;
        }
      }
    }
  }
}
</style>
